<template>
  <div>
    <!-- Header -->
    <div class="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-3 mb-6">
      <div class="flex items-center gap-1.5">
        <div class="analytics-date-pill">
          <AnalyticsDateRangePicker v-model="dateRange" useQueryParams />
        </div>
        <span v-if="previousPeriodStartDate" class="text-xs font-medium text-gray-400">vs</span>
        <Selector
          v-if="previousPeriodStartDate"
          id="comparison-type-selector"
          name="comparisonType"
          size="small"
          v-model="comparisonType"
          :options="comparisonTypeOptions"
        />
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <MultiSelect
          name="categories"
          :label="t('filter_categories')"
          :options="categoryOptions"
          :modelValue="selectedCategories"
          @update:model-value="handleCategoriesUpdate"
        />
        <MultiSelect
          v-if="isInsurerAdmin"
          name="insurer-manager"
          :label="t('filter_sales_manager')"
          :options="salesManagerOptions"
          :modelValue="selectedSalesManagers"
          @update:model-value="handleSalesManagersUpdate"
        />
        <div class="w-full sm:w-72">
          <AnalyticsSelector @update-selections="handleSelectionsUpdate" />
        </div>
      </div>
    </div>

    <!-- Chart + KPI cards -->
    <div class="bg-white p-6 rounded-lg shadow-lg">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DailyContractsChart class="lg:col-span-2" :loading="loading" :chartView="chartView" />
        <CategoryRepartition
          class="lg:border-l lg:border-gray-100 lg:pl-6"
          :loading="loading"
          :repartition="generalAnalytics?.categoryRepartition"
          :comparisonType="comparisonType"
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 border-t border-gray-100 pt-4">
        <DataCardWithVariation
          v-if="generalData?.todayContracts"
          :title="t('today_contracts')"
          :tooltipText="t('today_contracts_tooltip')"
          :data="generalData.todayContracts"
        />
        <DataCardWithVariation
          v-if="generalData?.premium"
          :title="t('premium')"
          :tooltipText="t('premium_tooltip')"
          :data="generalData.premium"
          :comparisonType="comparisonType"
        />
        <DataCardWithVariation
          v-if="generalData?.resiliationRate"
          :title="t('resiliation_rate')"
          :tooltipText="t('resiliation_rate_tooltip')"
          :data="generalData.resiliationRate"
          :comparisonType="comparisonType"
        />
      </div>
    </div>

    <div v-if="generalAnalytics" class="mt-4">
      <AnalyticsSalesRepartition :analytics="generalAnalytics" :showVerticalRepartition="false" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import {
  AnalyticsDateRangePicker,
  AnalyticsSalesRepartition,
  DataCardWithVariation,
  MultiSelect,
  Selector,
  currentMonthRange,
  inputDateFormat,
  currentWeek,
  currentYear,
  currentMonth,
} from "@estaly/ui";
import { differenceInMonths } from "date-fns";
import DailyContractsChart from "@/components/DailyContractsChart.vue";
import AnalyticsSelector from "@/components/AnalyticsSelector.vue";
import CategoryRepartition from "@/components/CategoryRepartition.vue";
import { FILTER_OPTIONS_PER_PAGE } from "@/constants";

export default {
  components: {
    AnalyticsDateRangePicker,
    AnalyticsSalesRepartition,
    DataCardWithVariation,
    MultiSelect,
    Selector,
    DailyContractsChart,
    AnalyticsSelector,
    CategoryRepartition,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      dateRange: currentMonthRange().map((date) => inputDateFormat(date)),
      selectedOrganizationId: null,
      selectedStoreIds: [],
      selectedCategories: [],
      selectedSalesManagers: [],
      loading: false,
      comparisonType: "previousMonth",
      chartView: "daily",
    };
  },

  computed: {
    ...mapState("analytics", ["generalAnalytics"]),
    ...mapGetters("analytics", ["generalData", "previousPeriodStartDate"]),
    ...mapGetters("contractDistributions", ["categoryOptions"]),
    ...mapGetters("insurerManagers", ["salesManagerOptions"]),
    ...mapGetters("auth", ["isInsurerAdmin"]),

    comparisonTypeOptions() {
      return [
        { value: "previousMonth", label: this.t("previousMonth") },
        { value: "previousPeriod", label: this.t("previousPeriod") },
        { value: "previousWeek", label: this.t("previousWeek") },
        { value: "previousYear", label: this.t("previousYear") },
      ];
    },
  },

  watch: {
    comparisonType() {
      this.refreshAnalytics();
    },
    dateRange(newValue) {
      const [startDate, endDate] = newValue;
      if (!startDate || !endDate) return;

      this.autoSwitchChartView(newValue);
      const defaultComparison = this.getDefaultComparisonTypeForRange(newValue);
      if (defaultComparison !== this.comparisonType) {
        this.comparisonType = defaultComparison;
      } else {
        this.refreshAnalytics();
      }
    },
  },

  created() {
    this.autoSwitchChartView(this.dateRange);
    this.fetchContractDistributions({ perPage: FILTER_OPTIONS_PER_PAGE });
    if (this.isInsurerAdmin) this.fetchInsurerManagers({ perPage: FILTER_OPTIONS_PER_PAGE });
    this.refreshAnalytics();
  },

  methods: {
    ...mapActions("analytics", ["fetchGeneralAnalytics"]),
    ...mapActions("contractDistributions", { fetchContractDistributions: "fetchIndex" }),
    ...mapActions("insurerManagers", { fetchInsurerManagers: "fetchIndex" }),

    autoSwitchChartView(dateRange) {
      const [startDate, endDate] = dateRange.map((d) => new Date(d));
      const monthsDifference = differenceInMonths(endDate, startDate);
      this.chartView = monthsDifference > 2 ? "monthly" : "daily";
    },

    getDefaultComparisonTypeForRange(dateRange) {
      const [startDate, endDate] = dateRange.map((d) => new Date(d));

      if (currentWeek(startDate, endDate)) return "previousWeek";
      if (currentYear(startDate, endDate)) return "previousYear";
      if (currentMonth(startDate, endDate)) return "previousMonth";

      return "previousPeriod";
    },

    async refreshAnalytics() {
      const [startDate, endDate] = this.dateRange;
      if (!startDate || !endDate) return;

      this.loading = true;
      const response = await this.fetchGeneralAnalytics({
        startDate,
        endDate,
        comparisonType: this.comparisonType,
        organizationId: this.selectedOrganizationId,
        stores: this.selectedStoreIds,
        categories: this.selectedCategories,
        insurerManagerIds: this.selectedSalesManagers,
      });
      // An outdated response must not stop the loader of the request still pending.
      if (response?.isLatest !== false) this.loading = false;
    },

    handleCategoriesUpdate(categories) {
      this.selectedCategories = categories;
      this.refreshAnalytics();
    },

    handleSalesManagersUpdate(salesManagerIds) {
      this.selectedSalesManagers = salesManagerIds;
      this.refreshAnalytics();
    },

    handleSelectionsUpdate(selections) {
      if (selections.organizations.length > 0) {
        this.selectedOrganizationId = selections.organizations[0].id;
        this.selectedStoreIds = [];
      } else if (selections.stores.length > 0) {
        this.selectedStoreIds = selections.stores.map((store) => store.id);
        this.selectedOrganizationId = null;
      } else {
        this.selectedOrganizationId = null;
        this.selectedStoreIds = [];
      }

      this.refreshAnalytics();
    },
  },
};
</script>

<style scoped>
.analytics-date-pill :deep(.dp__input_icon) {
  display: none !important;
}

.analytics-date-pill :deep(.dp__input) {
  font-size: 12px !important;
  padding: 5px 10px !important;
  padding-right: 24px !important;
  height: auto !important;
  min-height: unset !important;
  line-height: 1.4 !important;
  border-radius: 0.5rem;
}

.analytics-date-pill :deep(.dp__clear_icon) {
  right: 4px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}
</style>

<i18n src="./Home.i18n.json"></i18n>
